import React, { useEffect, useMemo, useState } from "react";
import { getReportPeriod } from "../reportPeriod";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import {
    deleteFinanceCapitalMovement,
    downloadFinanceExport,
    getFinanceFixedCharges,
    getFinanceFulfillmentSuppliers,
    getFinanceLegalEntitySettings,
    getFinanceTabReport,
    importFinanceExcel,
    recalculateFinanceReport,
    upsertFinanceFixedCharge,
    upsertFinanceFulfillmentSupplier,
    upsertFinanceLegalEntitySetting,
    upsertFinanceCapitalMovement,
    upsertFinanceReportNote,
    upsertFinanceReportValue,
    uploadFinanceExcel,
} from "../api/financeReportsApi";

const pageStyle = { display: "grid", gap: 16 };
const panelStyle = { background: "#fff", border: "1px solid #d9dee8", borderRadius: 8, padding: 16 };
const controlBaseStyle = { border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 10px", fontSize: 14, background: "#fff", boxSizing: "border-box" };
const inputStyle = { ...controlBaseStyle, width: "100%" };
const filterInputStyle = { ...controlBaseStyle, minWidth: 150 };
const filterSelectStyle = { ...controlBaseStyle, minWidth: 280 };
const buttonStyle = {
    border: "1px solid #cbd5e1",
    borderRadius: 10,
    padding: "9px 14px",
    fontSize: "13.5px",
    background: "#fff",
    color: "#475569",
    boxSizing: "border-box",
    width: "auto",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
};
const formButtonStyle = { ...buttonStyle, width: "100%" };
const primaryButtonStyle = { ...buttonStyle, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const primaryFormButtonStyle = { ...formButtonStyle, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const thStyle = { textAlign: "left", padding: "9px 10px", borderBottom: "1px solid #e2e8f0", color: "#475569", fontSize: 11, textTransform: "uppercase", whiteSpace: "nowrap" };
const tdStyle = { padding: "9px 10px", borderBottom: "1px solid #eef2f7", verticalAlign: "top" };
const amountStyle = { ...tdStyle, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const mutedStyle = { color: "#64748b", fontSize: 12 };
const groupColors = {
    sales: { background: "#e0f2fe", color: "#075985" },
    margin: { background: "#dcfce7", color: "#166534" },
    expenses: { background: "#fee2e2", color: "#991b1b" },
    taxes: { background: "#fef3c7", color: "#92400e" },
    result: { background: "#ede9fe", color: "#5b21b6" },
    fcf: { background: "#ede9fe", color: "#5b21b6" },
    opening: { background: "#e2e8f0", color: "#334155" },
    inflows: { background: "#dcfce7", color: "#166534" },
    outflows: { background: "#fee2e2", color: "#991b1b" },
    closing: { background: "#e0f2fe", color: "#075985" },
    movement: { background: "#fef3c7", color: "#92400e" },
    mapped: { background: "#dcfce7", color: "#166534" },
    unmapped: { background: "#ffedd5", color: "#9a3412" },
    capital_flow: { background: "#e0e7ff", color: "#3730a3" },
    profit: { background: "#f3e8ff", color: "#6b21a8" },
    working_capital: { background: "#f1f5f9", color: "#334155" },
};

const today = new Date();
const { periodFrom: defaultPeriodFrom, periodTo: defaultPeriodTo } = getReportPeriod("current_month", today);

const tabs = [
    ["pnl", "P&L"],
    ["cash_flow", "Cash Flow"],
    ["deposits", "Депозиты"],
    ["supplier_payments", "Оплата поставщикам"],
    ["capital", "Capital"],
    ["settings", "Настройки"],
    ["import", "Импорт"],
];

const taxProfileLabels = {
    none: "Без налога",
    fop_group_2_fixed: "ФОП 2 группа",
    fop_group_3_6_percent: "ФОП 3 группа 6%",
    custom: "Custom",
};

const ownerOptions = ["Петренко Ира", "Наздрин Саша", "Наздрин Дима"];

const formatAmount = (value) => {
    const number = Number(value || 0);
    if (!Number.isFinite(number)) {
        return String(value || "0");
    }
    return number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatValue = (value, kind = "amount") => {
    if (kind === "percent") {
        const number = Number(value || 0) * 100;
        if (!Number.isFinite(number)) {
            return "0,00%";
        }
        return `${number.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    }
    return formatAmount(value);
};

const formatApiError = (err, fallback) => {
    const detail = err?.response?.data?.detail;
    if (Array.isArray(detail)) {
        return detail.map((item) => item.msg || JSON.stringify(item)).join("; ");
    }
    return detail || err?.message || fallback;
};

const Field = ({ label, children }) => (
    <label style={{ display: "grid", gap: 6, minWidth: 0, fontSize: 13, fontWeight: 800, color: "#334155" }}>
        <span>{label}</span>
        {children}
    </label>
);

const CellEditor = ({ row, cell, businessEntityId, onSaved }) => {
    const [manualAmount, setManualAmount] = useState(cell.manual_amount || "");
    const [note, setNote] = useState(cell.note || "");
    const [editing, setEditing] = useState(false);
    const save = async () => {
        if (!businessEntityId) {
            return;
        }
        await upsertFinanceReportValue({
            period_month: cell.period_month,
            line_code: row.code,
            business_entity_id: businessEntityId || null,
            manual_amount: manualAmount === "" ? null : manualAmount,
        });
        await upsertFinanceReportNote({
            period_month: cell.period_month,
            line_code: row.code,
            business_entity_id: businessEntityId || null,
            note,
        });
        setEditing(false);
        await onSaved();
    };
    if (!businessEntityId) {
        return (
            <div
                style={{ textAlign: "right", width: "100%" }}
                title="Ручные корректировки доступны только после выбора юрлица."
            >
                <div style={{ fontWeight: cell.has_manual ? 900 : 600 }}>{formatValue(cell.final_amount, row.kind)}</div>
                {cell.has_manual && <div style={mutedStyle}>ручн.</div>}
                {cell.note && <div style={{ ...mutedStyle, color: "#2563eb" }}>примеч.</div>}
            </div>
        );
    }
    if (!editing) {
        return (
            <button
                type="button"
                onClick={() => setEditing(true)}
                style={{ border: 0, background: "transparent", cursor: "pointer", textAlign: "right", width: "100%", font: "inherit" }}
                title={cell.note || (cell.updated_at ? `Редактировать. Изменено: ${cell.updated_at}` : "Редактировать")}
            >
                <div style={{ fontWeight: cell.has_manual ? 900 : 600 }}>{formatValue(cell.final_amount, row.kind)}</div>
                {cell.has_manual && <div style={mutedStyle}>ручн.</div>}
                {cell.note && <div style={{ ...mutedStyle, color: "#2563eb" }}>примеч.</div>}
            </button>
        );
    }
    return (
        <div style={{ display: "grid", gap: 6, minWidth: 180 }}>
            <input style={inputStyle} value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} placeholder={cell.calculated_amount} />
            <textarea style={{ ...inputStyle, minHeight: 62 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Примечание" />
            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <button type="button" style={buttonStyle} onClick={() => setEditing(false)}>Отмена</button>
                <button type="button" style={primaryButtonStyle} onClick={save}>OK</button>
            </div>
        </div>
    );
};

const ReportTable = ({ report, businessEntityId, onSaved }) => {
    if (!report) {
        return <div style={panelStyle}>Нет данных.</div>;
    }
    const rowsWithGroups = [];
    let lastGroup = "";
    report.rows.forEach((row) => {
        const groupKey = row.group_key || "other";
        if (groupKey !== lastGroup) {
            rowsWithGroups.push({ type: "group", key: `${groupKey}-${rowsWithGroups.length}`, groupKey, label: row.group_label || "Другое" });
            lastGroup = groupKey;
        }
        rowsWithGroups.push({ type: "row", row });
    });
    return (
        <section style={{ ...panelStyle, overflowX: "auto" }}>
            {!businessEntityId && (
                <div style={{ ...mutedStyle, marginBottom: 10 }}>
                    Ручные корректировки доступны только по выбранному юрлицу. В режиме “Все юрлица” отчет собирает сумму и корректировки по юрлицам.
                </div>
            )}
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Статья</th>
                        {report.months.map((month) => <th key={month} style={{ ...thStyle, textAlign: "right" }}>{month.slice(0, 7)}</th>)}
                        <th style={{ ...thStyle, textAlign: "right" }}>Итого</th>
                    </tr>
                </thead>
                <tbody>
                    {rowsWithGroups.map((item) => {
                        if (item.type === "group") {
                            const colors = groupColors[item.groupKey] || { background: "#f8fafc", color: "#334155" };
                            return (
                                <tr key={item.key}>
                                    <td colSpan={report.months.length + 2} style={{ padding: "10px 12px", background: colors.background, color: colors.color, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0, borderTop: "1px solid #dbe4ef", borderBottom: "1px solid #dbe4ef" }}>
                                        {item.label}
                                    </td>
                                </tr>
                            );
                        }
                        const row = item.row;
                        const colors = groupColors[row.group_key] || {};
                        const isTotal = row.kind === "total" || row.kind === "subtotal";
                        return (
                            <tr key={row.code} style={{ background: isTotal ? colors.background || "#f8fafc" : "#fff" }}>
                                <td style={{ ...tdStyle, minWidth: 300 }}>
                                    <div
                                        style={{ fontWeight: isTotal ? 900 : 700, color: isTotal ? colors.color || "#0f172a" : "#0f172a" }}
                                        title={row.code === "pnl_other_payment_expenses" ? "Сумма платежей категории «Прочие расходы». Не дублируйте эти платежи в ручных статьях прочих расходов." : undefined}
                                    >{row.label}</div>
                                </td>
                                {row.cells.map((cell) => (
                                    <td key={`${row.code}-${cell.period_month}`} style={amountStyle}>
                                        <CellEditor row={row} cell={cell} businessEntityId={businessEntityId} onSaved={onSaved} />
                                    </td>
                                ))}
                                <td style={{ ...amountStyle, fontWeight: 900, color: isTotal ? colors.color || "#0f172a" : "#0f172a" }}>{formatValue(row.total, row.kind)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </section>
    );
};

const DetailTable = ({ title, columns, rows }) => {
    if (!rows) {
        return null;
    }
    return (
        <section style={panelStyle}>
            <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>{title}</h3>
            <div style={{ overflowX: "auto" }}>
                {rows.length === 0 ? (
                    <div style={{ ...mutedStyle, padding: "8px 0" }}>Нет данных за выбранный период.</div>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th key={column.key} style={{ ...thStyle, textAlign: column.align || "left" }}>{column.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={row.id || row.supplier_code || row.owner_name || index}>
                                    {columns.map((column) => (
                                        <td key={column.key} style={column.align === "right" ? amountStyle : tdStyle}>
                                            {column.format === "amount" ? formatAmount(row[column.key]) : row[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
};

// Свой склад: продажи / FIFO-себестоимость / валовая прибыль по складам (строка P&L «Продажи Свой склад»).
const OwnWarehouseSalesBlock = ({ rows, totals }) => {
    if (!rows) {
        return null;
    }
    const metrics = [
        { label: "Продажи", value: formatAmount(totals?.sales_amount) },
        { label: "FIFO-себестоимость", value: formatAmount(totals?.cost_amount) },
        { label: "Валовая прибыль", value: formatAmount(totals?.gross_profit) },
        { label: "Заказов", value: String(totals?.order_count ?? 0) },
    ];
    const fifoLines = Number(totals?.fifo_lines || 0);
    const totalLines = Number(totals?.total_lines || 0);
    const estimateLines = Math.max(totalLines - fifoLines, 0);
    return (
        <section style={panelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 18 }}>Свой склад: продажи / FIFO-себестоимость / валовая прибыль по складам</h3>
                    <div style={{ ...mutedStyle, marginTop: 4 }}>
                        Себестоимость — фактический FIFO списанных партий; до списания в строке стоит оценка.
                    </div>
                </div>
                {totalLines > 0 && (
                    <div style={mutedStyle}>
                        Позиций с фактическим FIFO: <strong style={{ color: "#334155" }}>{fifoLines}</strong> из {totalLines}
                        {estimateLines > 0 ? ` (ещё по оценке: ${estimateLines})` : ""}
                    </div>
                )}
            </div>
            {rows.length === 0 ? (
                <div style={{ ...mutedStyle, padding: "8px 0" }}>Нет продаж со своих складов за выбранный период.</div>
            ) : (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 12 }}>
                        {metrics.map((metric) => (
                            <div key={metric.label} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                                <div style={{ ...mutedStyle, fontWeight: 800, textTransform: "uppercase", fontSize: 11 }}>{metric.label}</div>
                                <div style={{ fontSize: 21, fontWeight: 800, color: "#0f172a", marginTop: 5 }}>{metric.value}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ overflowX: "auto" }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Код</th>
                                    <th style={thStyle}>Склад</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Заказов</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Продажи</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>FIFO-себестоимость</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Валовая прибыль</th>
                                    <th style={{ ...thStyle, textAlign: "right" }}>Позиций FIFO / всего</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.warehouse_code}>
                                        <td style={tdStyle}>{row.warehouse_code}</td>
                                        <td style={tdStyle}>{row.warehouse_name}</td>
                                        <td style={amountStyle}>{row.order_count}</td>
                                        <td style={amountStyle}>{formatAmount(row.sales_amount)}</td>
                                        <td style={amountStyle}>{formatAmount(row.cost_amount)}</td>
                                        <td style={amountStyle}>{formatAmount(row.gross_profit)}</td>
                                        <td style={amountStyle}>{row.fifo_lines} / {row.total_lines}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </section>
    );
};

const FounderBalanceBlock = ({ title, subtitle, summary, cumulativePeriodFrom, accentColor, metrics }) => (
    <section style={{ ...panelStyle, borderTop: `4px solid ${accentColor}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
            <div>
                <h3 style={{ margin: 0, fontSize: 18 }}>{title}</h3>
                <div style={{ ...mutedStyle, marginTop: 4 }}>{subtitle}</div>
            </div>
            <div style={mutedStyle}>Накопительно с {cumulativePeriodFrom?.slice(0, 7) || "начала учёта"}</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 12 }}>
            {metrics.map((metric) => (
                <div key={metric.label} style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, background: "#fff" }}>
                    <div style={{ ...mutedStyle, fontWeight: 800, textTransform: "uppercase", fontSize: 11 }}>{metric.label}</div>
                    <div style={{ fontSize: 21, fontWeight: 800, color: "#0f172a", marginTop: 5 }}>{formatAmount(summary?.[metric.periodKey])}</div>
                    <div style={{ ...mutedStyle, marginTop: 6 }}>За период · накопительно: <strong style={{ color: "#334155" }}>{formatAmount(summary?.[metric.cumulativeKey])}</strong></div>
                </div>
            ))}
        </div>
    </section>
);

const CapitalMovementPanel = ({ report, organizations, businessEntityId, periodFrom, onSaved }) => {
    const details = report?.details || {};
    const [draft, setDraft] = useState({
        owner_name: ownerOptions[0],
        period_month: periodFrom,
        movement_type: "capital_contribution",
        amount: "",
        comment: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const movements = details.capital_movements || [];
    const selectedEntity = organizations.find((item) => String(item.id) === String(businessEntityId));

    useEffect(() => {
        setDraft((current) => ({ ...current, period_month: periodFrom }));
    }, [periodFrom]);

    const saveMovement = async () => {
        if (!businessEntityId) {
            setError("Выберите юрлицо. Движения капитала нельзя вводить в режиме Все юрлица.");
            return;
        }
        if (!draft.amount || Number(draft.amount) <= 0) {
            setError("Введите сумму больше нуля.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const isContribution = draft.movement_type === "capital_contribution";
            const isProfitWithdrawal = draft.movement_type === "profit_withdrawal";
            await upsertFinanceCapitalMovement({
                owner_name: draft.owner_name,
                business_entity_id: Number(businessEntityId),
                period_month: draft.period_month,
                direction: isContribution ? "in" : "out",
                amount: draft.amount,
                affects_profit_distribution: isProfitWithdrawal,
                affects_cash_flow: true,
                comment: draft.comment,
            });
            setDraft((current) => ({ ...current, amount: "", comment: "" }));
            await onSaved();
        } catch (err) {
            setError(formatApiError(err, "Не удалось сохранить движение капитала."));
        } finally {
            setSaving(false);
        }
    };

    const deleteMovement = async (movementId) => {
        setSaving(true);
        setError("");
        try {
            await deleteFinanceCapitalMovement(movementId);
            await onSaved();
        } catch (err) {
            setError(formatApiError(err, "Не удалось удалить движение капитала."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section style={panelStyle}>
            <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>Движения капитала по учредителям</h3>
            {!businessEntityId && (
                <div style={{ ...mutedStyle, marginBottom: 10 }}>
                    Для ввода или удаления движения выберите юрлицо. В режиме “Все юрлица” показывается суммарная картина.
                </div>
            )}
            {error && <div style={{ color: "#b91c1c", marginBottom: 10 }}>{error}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 14 }}>
                <Field label="Юрлицо">
                    <input style={inputStyle} value={selectedEntity?.short_name || "Выберите юрлицо в фильтре"} disabled />
                </Field>
                <Field label="Учредитель">
                    <select
                        style={inputStyle}
                        value={draft.owner_name}
                        onChange={(event) => setDraft((current) => ({ ...current, owner_name: event.target.value }))}
                        disabled={!businessEntityId || saving}
                    >
                        {ownerOptions.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
                    </select>
                </Field>
                <Field label="Месяц">
                    <input
                        style={inputStyle}
                        type="date"
                        value={draft.period_month}
                        onChange={(event) => setDraft((current) => ({ ...current, period_month: event.target.value }))}
                        disabled={!businessEntityId || saving}
                    />
                </Field>
                <Field label="Операция">
                    <select
                        style={inputStyle}
                        value={draft.movement_type}
                        onChange={(event) => setDraft((current) => ({ ...current, movement_type: event.target.value }))}
                        disabled={!businessEntityId || saving}
                    >
                        <option value="capital_contribution">Ввод капитала</option>
                        <option value="capital_withdrawal">Вывод капитала</option>
                        <option value="profit_withdrawal">Вывод прибыли</option>
                    </select>
                </Field>
                <Field label="Сумма">
                    <input
                        style={inputStyle}
                        type="number"
                        min="0"
                        step="0.01"
                        value={draft.amount}
                        onChange={(event) => setDraft((current) => ({ ...current, amount: event.target.value }))}
                        disabled={!businessEntityId || saving}
                    />
                </Field>
                <Field label="Комментарий">
                    <input
                        style={inputStyle}
                        value={draft.comment}
                        onChange={(event) => setDraft((current) => ({ ...current, comment: event.target.value }))}
                        disabled={!businessEntityId || saving}
                    />
                </Field>
                <div style={{ display: "flex", alignItems: "end" }}>
                    <button type="button" style={primaryFormButtonStyle} onClick={saveMovement} disabled={!businessEntityId || saving}>
                        Добавить
                    </button>
                </div>
            </div>
            <div style={{ overflowX: "auto" }}>
                {movements.length === 0 ? (
                    <div style={{ ...mutedStyle, padding: "8px 0" }}>Движений капитала за выбранный период нет.</div>
                ) : (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Месяц</th>
                                <th style={thStyle}>Юрлицо</th>
                                <th style={thStyle}>Учредитель</th>
                                <th style={thStyle}>Операция</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Сумма</th>
                                <th style={thStyle}>Комментарий</th>
                                <th style={{ ...thStyle, textAlign: "right" }}>Действие</th>
                            </tr>
                        </thead>
                        <tbody>
                            {movements.map((item) => {
                                const operation = item.direction === "in"
                                    ? "Ввод капитала"
                                    : item.affects_profit_distribution
                                        ? "Вывод прибыли"
                                        : "Вывод капитала";
                                return (
                                    <tr key={item.id}>
                                        <td style={tdStyle}>{item.period_month?.slice(0, 7)}</td>
                                        <td style={tdStyle}>{item.business_entity_name || "Все юрлица"}</td>
                                        <td style={tdStyle}>{item.owner_name}</td>
                                        <td style={tdStyle}>{operation}</td>
                                        <td style={amountStyle}>{formatAmount(item.amount)}</td>
                                        <td style={tdStyle}>{item.comment || ""}</td>
                                        <td style={{ ...amountStyle, minWidth: 110 }}>
                                            {businessEntityId && item.business_entity_id === Number(businessEntityId) ? (
                                                <button type="button" style={buttonStyle} onClick={() => deleteMovement(item.id)} disabled={saving}>
                                                    Удалить
                                                </button>
                                            ) : (
                                                <span style={mutedStyle}>только просмотр</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </section>
    );
};

const ReportDetails = ({ activeTab, report, organizations, businessEntityId, periodFrom, onSaved }) => {
    const details = report?.details || {};
    if (activeTab === "pnl") {
        return (
            <>
                <DetailTable
                    title="Расшифровка Продаж Опт по отправителям"
                    rows={details.fulfillment_sales_by_supplier}
                    columns={[
                        { key: "supplier_code", label: "Код" },
                        { key: "supplier_name", label: "Отправитель" },
                        { key: "order_count", label: "Заказов", align: "right" },
                        { key: "sales_amount", label: "Продажи", align: "right", format: "amount" },
                        { key: "cost_amount", label: "Себестоимость", align: "right", format: "amount" },
                        { key: "gross_profit", label: "Маржа", align: "right", format: "amount" },
                    ]}
                />
                <OwnWarehouseSalesBlock rows={details.own_warehouse_sales_by_warehouse} totals={details.own_warehouse_totals} />
                <DetailTable
                    title="Расшифровка Общих продаж по поставщикам"
                    rows={details.general_sales_by_supplier}
                    columns={[
                        { key: "supplier_code", label: "Код" },
                        { key: "supplier_name", label: "Поставщик" },
                        { key: "order_count", label: "Заказов", align: "right" },
                        { key: "sales_amount", label: "Продажи", align: "right", format: "amount" },
                        { key: "cost_amount", label: "Себестоимость", align: "right", format: "amount" },
                        { key: "gross_profit", label: "Маржа", align: "right", format: "amount" },
                    ]}
                />
            </>
        );
    }
    if (activeTab === "deposits") {
        const rows = (details.supplier_deposits || []).map((item) => ({
            id: item.id,
            name: item.name,
            suppliers: (item.active_links || []).map((link) => `${link.supplier_name || link.supplier_code} (${link.supplier_code})`).join(", "),
            opening_balance: item.opening_balance,
            topups: item.topups?.amount,
            writeoffs: item.writeoffs?.amount,
            calculated_balance: item.calculated_balance,
        }));
        return (
            <DetailTable
                title="Депозиты по отправителям"
                rows={rows}
                columns={[
                    { key: "name", label: "Группа" },
                    { key: "suppliers", label: "Отправители" },
                    { key: "opening_balance", label: "Начало", align: "right", format: "amount" },
                    { key: "topups", label: "Пополнения", align: "right", format: "amount" },
                    { key: "writeoffs", label: "Списания", align: "right", format: "amount" },
                    { key: "calculated_balance", label: "Конец", align: "right", format: "amount" },
                ]}
            />
        );
    }
    if (activeTab === "supplier_payments") {
        return (
            <DetailTable
                title="Оплаты поставщикам по поставщикам"
                rows={details.supplier_payments_by_supplier}
                columns={[
                    { key: "supplier_code", label: "Код" },
                    { key: "supplier_name", label: "Поставщик" },
                    { key: "payment_count", label: "Платежей", align: "right" },
                    { key: "amount", label: "Сумма", align: "right", format: "amount" },
                ]}
            />
        );
    }
    if (activeTab === "capital") {
        const capitalSummary = details.capital_summary || {};
        const profitSummary = details.profit_summary || {};
        return (
            <>
                <FounderBalanceBlock
                    title="Капитал учредителей"
                    subtitle="Вложения в бизнес и возврат вложенных средств. Прибыль сюда не входит."
                    summary={capitalSummary}
                    cumulativePeriodFrom={details.cumulative_period_from}
                    accentColor="#2563eb"
                    metrics={[
                        { label: "Введено капитала", periodKey: "contribution", cumulativeKey: "contribution_cumulative" },
                        { label: "Выведено капитала", periodKey: "withdrawal", cumulativeKey: "withdrawal_cumulative" },
                        { label: "Остаток капитала", periodKey: "net_flow", cumulativeKey: "net_flow_cumulative" },
                    ]}
                />
                <DetailTable
                    title="Капитал по учредителям"
                    rows={details.capital_owners}
                    columns={[
                        { key: "owner_name", label: "Учредитель" },
                        { key: "profit_share_percent", label: "Доля", align: "right" },
                        { key: "capital_contribution", label: "Ввод за период", align: "right", format: "amount" },
                        { key: "capital_withdrawal", label: "Вывод за период", align: "right", format: "amount" },
                        { key: "capital_contribution_cumulative", label: "Ввод накопительно", align: "right", format: "amount" },
                        { key: "capital_withdrawal_cumulative", label: "Вывод накопительно", align: "right", format: "amount" },
                        { key: "capital_remaining_cumulative", label: "Остаток капитала", align: "right", format: "amount" },
                    ]}
                />
                <FounderBalanceBlock
                    title="Прибыль учредителей"
                    subtitle="Начисление доли чистой прибыли, выплаты и остаток к выплате."
                    summary={profitSummary}
                    cumulativePeriodFrom={details.cumulative_period_from}
                    accentColor="#7c3aed"
                    metrics={[
                        { label: "Начислено прибыли", periodKey: "accrued", cumulativeKey: "accrued_cumulative" },
                        { label: "Выплачено прибыли", periodKey: "withdrawal", cumulativeKey: "withdrawal_cumulative" },
                        { label: "Остаток к выплате", periodKey: "remaining", cumulativeKey: "remaining_cumulative" },
                    ]}
                />
                <DetailTable
                    title="Прибыль по учредителям"
                    rows={details.profit_owners}
                    columns={[
                        { key: "owner_name", label: "Учредитель" },
                        { key: "profit_share_percent", label: "Доля", align: "right" },
                        { key: "profit_accrued", label: "Начислено за период", align: "right", format: "amount" },
                        { key: "profit_withdrawal", label: "Выплачено за период", align: "right", format: "amount" },
                        { key: "profit_remaining", label: "Изменение остатка", align: "right", format: "amount" },
                        { key: "profit_accrued_cumulative", label: "Начислено накопительно", align: "right", format: "amount" },
                        { key: "profit_withdrawal_cumulative", label: "Выплачено накопительно", align: "right", format: "amount" },
                        { key: "profit_remaining_cumulative", label: "Остаток к выплате", align: "right", format: "amount" },
                    ]}
                />
                <CapitalMovementPanel
                    report={report}
                    organizations={organizations}
                    businessEntityId={businessEntityId}
                    periodFrom={periodFrom}
                    onSaved={onSaved}
                />
            </>
        );
    }
    return null;
};

const SettingsPanel = ({ organizations, settings, fixedCharges, suppliers, reloadSettings }) => {
    const [legalDraft, setLegalDraft] = useState({ business_entity_id: "", tax_profile: "none", turnover_tax_rate: "0", esv_enabled: false, tax_year: new Date().getFullYear(), valid_from: "", esv_monthly_amount: "0", single_tax_monthly_amount: "0", military_levy_monthly_amount: "0", profit_share_percent: "0", notes: "" });
    const [chargeDraft, setChargeDraft] = useState({ business_entity_id: "", line_code: "pnl_tabletki_fixed", valid_from: defaultPeriodFrom, valid_to: "", monthly_amount: "0", notes: "", is_active: true });
    const [supplierDraft, setSupplierDraft] = useState({ supplier_code: "D15", label: "", is_active: true, valid_from: "", valid_to: "", notes: "" });
    const [saveStatus, setSaveStatus] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saving, setSaving] = useState(false);

    const runSave = async (action, successText) => {
        setSaving(true);
        setSaveStatus("");
        setSaveError("");
        try {
            await action();
            await reloadSettings();
            setSaveStatus(successText);
        } catch (err) {
            setSaveError(formatApiError(err, "Не удалось сохранить настройки."));
        } finally {
            setSaving(false);
        }
    };

    const saveLegal = async () => {
        await runSave(
            () => upsertFinanceLegalEntitySetting({
                ...legalDraft,
                business_entity_id: Number(legalDraft.business_entity_id),
                turnover_tax_rate: legalDraft.tax_profile === "fop_group_2_fixed" ? "0" : legalDraft.turnover_tax_rate,
                esv_enabled: Number(legalDraft.esv_monthly_amount || 0) > 0,
                tax_year: legalDraft.tax_year ? Number(legalDraft.tax_year) : null,
                valid_from: legalDraft.valid_from || null,
            }),
            "Налоговые настройки сохранены.",
        );
    };
    const saveCharge = async () => {
        await runSave(
            () => upsertFinanceFixedCharge({ ...chargeDraft, business_entity_id: Number(chargeDraft.business_entity_id), valid_to: chargeDraft.valid_to || null }),
            "Фиксированный расход сохранен.",
        );
    };
    const saveSupplier = async () => {
        await runSave(
            () => upsertFinanceFulfillmentSupplier({ ...supplierDraft, valid_from: supplierDraft.valid_from || null, valid_to: supplierDraft.valid_to || null }),
            "Отправитель фулфилмента сохранен.",
        );
    };

    return (
        <div style={{ display: "grid", gap: 16 }}>
            <section style={panelStyle}>
                <h2 style={{ marginTop: 0 }}>Налоги</h2>
                {saveStatus && <div style={{ ...mutedStyle, color: "#047857", marginBottom: 10 }}>{saveStatus}</div>}
                {saveError && <div style={{ ...mutedStyle, color: "#b91c1c", marginBottom: 10 }}>{saveError}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                    <Field label="Юрлицо">
                        <select style={inputStyle} value={legalDraft.business_entity_id} onChange={(e) => setLegalDraft({ ...legalDraft, business_entity_id: e.target.value })}>
                            <option value="">Выберите юрлицо</option>
                            {organizations.map((item) => <option key={item.id} value={item.id}>{item.short_name}</option>)}
                        </select>
                    </Field>
                    <Field label="Налоговый профиль">
                        <select style={inputStyle} value={legalDraft.tax_profile} onChange={(e) => setLegalDraft({ ...legalDraft, tax_profile: e.target.value, turnover_tax_rate: e.target.value === "fop_group_2_fixed" ? "0" : legalDraft.turnover_tax_rate })}>
                            <option value="none">Без налога</option>
                            <option value="fop_group_2_fixed">ФОП 2 группа</option>
                            <option value="fop_group_3_6_percent">ФОП 3 группа 6%</option>
                        </select>
                    </Field>
                    <Field label="Ставка налога от оборота">
                        <input style={inputStyle} value={legalDraft.turnover_tax_rate} onChange={(e) => setLegalDraft({ ...legalDraft, turnover_tax_rate: e.target.value })} placeholder="0.06" disabled={legalDraft.tax_profile === "fop_group_2_fixed"} />
                    </Field>
                    <Field label="Год налогов">
                        <input style={inputStyle} value={legalDraft.tax_year || ""} onChange={(e) => setLegalDraft({ ...legalDraft, tax_year: e.target.value })} placeholder="2026" />
                    </Field>
                    <Field label="Действует с">
                        <input type="date" style={inputStyle} value={legalDraft.valid_from} onChange={(e) => setLegalDraft({ ...legalDraft, valid_from: e.target.value })} />
                    </Field>
                    <Field label="ЕСВ в месяц">
                        <input style={inputStyle} value={legalDraft.esv_monthly_amount} onChange={(e) => setLegalDraft({ ...legalDraft, esv_monthly_amount: e.target.value })} placeholder="1760" />
                    </Field>
                    <Field label="Единый налог в месяц">
                        <input style={inputStyle} value={legalDraft.single_tax_monthly_amount} onChange={(e) => setLegalDraft({ ...legalDraft, single_tax_monthly_amount: e.target.value })} disabled={legalDraft.tax_profile !== "fop_group_2_fixed"} />
                    </Field>
                    <Field label="Военный сбор в месяц">
                        <input style={inputStyle} value={legalDraft.military_levy_monthly_amount} onChange={(e) => setLegalDraft({ ...legalDraft, military_levy_monthly_amount: e.target.value })} disabled={legalDraft.tax_profile !== "fop_group_2_fixed"} />
                    </Field>
                    <button type="button" style={primaryFormButtonStyle} onClick={saveLegal} disabled={!legalDraft.business_entity_id || saving}>{saving ? "Сохранение..." : "Сохранить"}</button>
                </div>
                <table style={{ ...tableStyle, marginTop: 14 }}>
                    <thead><tr><th style={tdStyle}>Юрлицо</th><th style={tdStyle}>Год</th><th style={tdStyle}>Действует с</th><th style={tdStyle}>Профиль</th><th style={amountStyle}>ЕСВ/мес.</th><th style={amountStyle}>Единый налог/мес.</th><th style={amountStyle}>Военный сбор/мес.</th></tr></thead>
                    <tbody>{settings.map((item) => (
                        <tr key={item.id}><td style={tdStyle}>{item.business_entity_name || item.business_entity_id}</td><td style={tdStyle}>{item.tax_year || "Все годы"}</td><td style={tdStyle}>{item.valid_from || "С начала года"}</td><td style={tdStyle}>{taxProfileLabels[item.tax_profile] || item.tax_profile}</td><td style={amountStyle}>{item.esv_monthly_amount}</td><td style={amountStyle}>{item.single_tax_monthly_amount}</td><td style={amountStyle}>{item.military_levy_monthly_amount}</td></tr>
                    ))}</tbody>
                </table>
            </section>
            <section style={panelStyle}>
                <h2 style={{ marginTop: 0 }}>Фиксированные расходы</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                    <Field label="Юрлицо">
                        <select style={inputStyle} value={chargeDraft.business_entity_id} onChange={(e) => setChargeDraft({ ...chargeDraft, business_entity_id: e.target.value })}>
                            <option value="">Выберите юрлицо</option>
                            {organizations.map((item) => <option key={item.id} value={item.id}>{item.short_name}</option>)}
                        </select>
                    </Field>
                    <Field label="Статья расхода">
                        <select style={inputStyle} value={chargeDraft.line_code} onChange={(e) => setChargeDraft({ ...chargeDraft, line_code: e.target.value })}>
                            <option value="pnl_tabletki_fixed">Tabletki фикс.</option>
                            <option value="pnl_salesdrive_fixed">SalesDrive фикс.</option>
                            <option value="pnl_rent">Аренда</option>
                            <option value="pnl_cash_register_rent">Аренда касс</option>
                            <option value="pnl_other_fixed_expenses">Прочие фикс.</option>
                        </select>
                    </Field>
                    <Field label="Действует с">
                        <input style={inputStyle} type="date" value={chargeDraft.valid_from} onChange={(e) => setChargeDraft({ ...chargeDraft, valid_from: e.target.value })} />
                    </Field>
                    <Field label="Действует по">
                        <input style={inputStyle} type="date" value={chargeDraft.valid_to} onChange={(e) => setChargeDraft({ ...chargeDraft, valid_to: e.target.value })} />
                    </Field>
                    <Field label="Сумма в месяц">
                        <input style={inputStyle} value={chargeDraft.monthly_amount} onChange={(e) => setChargeDraft({ ...chargeDraft, monthly_amount: e.target.value })} />
                    </Field>
                    <button type="button" style={primaryFormButtonStyle} onClick={saveCharge} disabled={!chargeDraft.business_entity_id || saving}>{saving ? "Сохранение..." : "Сохранить"}</button>
                </div>
                <table style={{ ...tableStyle, marginTop: 14 }}>
                    <thead><tr><th style={tdStyle}>Юрлицо</th><th style={tdStyle}>Статья</th><th style={tdStyle}>Действует с</th><th style={tdStyle}>Действует по</th><th style={amountStyle}>Сумма в месяц</th></tr></thead>
                    <tbody>{fixedCharges.map((item) => (
                        <tr key={item.id}><td style={tdStyle}>{item.business_entity_name}</td><td style={tdStyle}>{item.line_label}</td><td style={tdStyle}>{item.valid_from}</td><td style={tdStyle}>{item.valid_to || "Бессрочно"}</td><td style={amountStyle}>{formatAmount(item.monthly_amount)}</td></tr>
                    ))}</tbody>
                </table>
            </section>
            <section style={panelStyle}>
                <h2 style={{ marginTop: 0 }}>Fulfillment suppliers</h2>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12 }}>
                    <Field label="Код поставщика">
                        <input style={inputStyle} value={supplierDraft.supplier_code} onChange={(e) => setSupplierDraft({ ...supplierDraft, supplier_code: e.target.value })} placeholder="D15" />
                    </Field>
                    <Field label="Название">
                        <input style={inputStyle} value={supplierDraft.label} onChange={(e) => setSupplierDraft({ ...supplierDraft, label: e.target.value })} placeholder="D15 fulfillment" />
                    </Field>
                    <button type="button" style={{ ...primaryButtonStyle, alignSelf: "end", justifySelf: "start" }} onClick={saveSupplier} disabled={saving}>{saving ? "Сохранение..." : "Сохранить"}</button>
                </div>
                <table style={{ ...tableStyle, marginTop: 14 }}><tbody>{suppliers.map((item) => (
                    <tr key={item.id}><td style={tdStyle}>{item.supplier_code}</td><td style={tdStyle}>{item.label}</td><td style={tdStyle}>{item.is_active ? "active" : "off"}</td></tr>
                ))}</tbody></table>
            </section>
        </div>
    );
};

const FinanceReportPage = () => {
    const [periodFrom, setPeriodFrom] = useState(defaultPeriodFrom);
    const [periodTo, setPeriodTo] = useState(defaultPeriodTo);
    const [periodPreset, setPeriodPreset] = useState("current_month");
    const [businessEntityId, setBusinessEntityId] = useState("");
    const [organizations, setOrganizations] = useState([]);
    const [activeTab, setActiveTab] = useState("pnl");
    const [report, setReport] = useState(null);
    const [settings, setSettings] = useState([]);
    const [fixedCharges, setFixedCharges] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [importFile, setImportFile] = useState(null);

    const applyPeriodPreset = (preset) => {
        setPeriodPreset(preset);
        if (preset === "custom") return;
        const range = getReportPeriod(preset);
        setPeriodFrom(range.periodFrom);
        setPeriodTo(range.periodTo);
    };

    const selectedBusinessEntityId = businessEntityId ? Number(businessEntityId) : null;
    const isReportTab = useMemo(() => ["pnl", "cash_flow", "deposits", "supplier_payments", "capital"].includes(activeTab), [activeTab]);

    const loadSettings = async () => {
        const [legalRows, fixedRows, supplierRows] = await Promise.all([
            getFinanceLegalEntitySettings(),
            getFinanceFixedCharges(),
            getFinanceFulfillmentSuppliers(),
        ]);
        setSettings(legalRows);
        setFixedCharges(fixedRows);
        setSuppliers(supplierRows);
    };

    const loadData = async () => {
        setLoading(true);
        setError("");
        try {
            const orgRows = await getBusinessOrganizations();
            setOrganizations(orgRows);
            if (isReportTab) {
                const reportData = await getFinanceTabReport({ tab: activeTab, periodFrom, periodTo, businessEntityId: selectedBusinessEntityId });
                setReport(reportData);
            }
            await loadSettings();
        } catch (err) {
            setError(formatApiError(err, "Не удалось загрузить фин отчет."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const runRecalculate = async () => {
        setLoading(true);
        setMessage("");
        setError("");
        try {
            const result = await recalculateFinanceReport({ periodFrom, periodTo, businessEntityId: selectedBusinessEntityId });
            setMessage(`Пересчитано строк: ${result.upserted_count || 0}`);
            await loadData();
        } catch (err) {
            setError(formatApiError(err, "Пересчет не выполнен."));
        } finally {
            setLoading(false);
        }
    };

    const runImport = async () => {
        setLoading(true);
        setMessage("");
        setError("");
        try {
            const result = importFile
                ? await uploadFinanceExcel({ file: importFile, businessEntityId: selectedBusinessEntityId })
                : await importFinanceExcel({ business_entity_id: selectedBusinessEntityId });
            setMessage(`Импортировано ячеек: ${result.imported_cells || 0}`);
            const nextPeriodFrom = result.period_from || periodFrom;
            const nextPeriodTo = result.period_to || periodTo;
            setPeriodFrom(nextPeriodFrom);
            setPeriodTo(nextPeriodTo);
            setPeriodPreset("custom");
            setActiveTab("pnl");
            const reportData = await getFinanceTabReport({ tab: "pnl", periodFrom: nextPeriodFrom, periodTo: nextPeriodTo, businessEntityId: selectedBusinessEntityId });
            setReport(reportData);
        } catch (err) {
            setError(formatApiError(err, "Импорт не выполнен."));
        } finally {
            setLoading(false);
        }
    };

    const runExport = async () => {
        setLoading(true);
        setMessage("");
        setError("");
        try {
            await downloadFinanceExport({ periodFrom, periodTo, businessEntityId: selectedBusinessEntityId, tab: isReportTab ? activeTab : null });
            setMessage("Экспорт XLSX сформирован.");
        } catch (err) {
            setError(formatApiError(err, "Экспорт XLSX не выполнен."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={pageStyle}>
            <section style={panelStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <h2 style={{ margin: 0 }}>Фин отчет</h2>
                        <p style={{ margin: "6px 0 0", color: "#64748b", fontSize: 13 }}>P&L, Cash Flow, депозиты, оплаты поставщикам и капитал.</p>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <select style={filterSelectStyle} value={periodPreset} onChange={(e) => applyPeriodPreset(e.target.value)}>
                            <option value="current_month">Текущий месяц (по сегодня)</option>
                            <option value="previous_month">Предыдущий месяц</option>
                            <option value="custom">Произвольный период</option>
                        </select>
                        <input style={filterInputStyle} type="date" value={periodFrom} onChange={(e) => { setPeriodFrom(e.target.value); setPeriodPreset("custom"); }} />
                        <input style={filterInputStyle} type="date" value={periodTo} onChange={(e) => { setPeriodTo(e.target.value); setPeriodPreset("custom"); }} />
                        <select style={filterSelectStyle} value={businessEntityId} onChange={(e) => setBusinessEntityId(e.target.value)}>
                            <option value="">Все юрлица</option>
                            {organizations.map((item) => <option key={item.id} value={item.id}>{item.short_name}</option>)}
                        </select>
                        <button type="button" style={primaryButtonStyle} onClick={loadData} disabled={loading}>Обновить</button>
                        <button type="button" style={buttonStyle} onClick={runRecalculate} disabled={loading}>Пересчитать</button>
                        <button type="button" style={buttonStyle} onClick={runExport} disabled={loading}>Экспорт XLSX</button>
                    </div>
                </div>
            </section>

            {error && <div style={{ ...panelStyle, borderColor: "#fecaca", color: "#b91c1c" }}>{error}</div>}
            {message && <div style={{ ...panelStyle, borderColor: "#bbf7d0", color: "#047857" }}>{message}</div>}

            <div className="og-tabbar">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={`og-tab${activeTab === key ? " active" : ""}`}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {isReportTab && (
                <>
                    <ReportTable report={report} businessEntityId={selectedBusinessEntityId} onSaved={loadData} />
                    <ReportDetails
                        activeTab={activeTab}
                        report={report}
                        organizations={organizations}
                        businessEntityId={selectedBusinessEntityId}
                        periodFrom={periodFrom}
                        onSaved={loadData}
                    />
                </>
            )}
            {activeTab === "settings" && (
                <SettingsPanel
                    organizations={organizations}
                    settings={settings}
                    fixedCharges={fixedCharges}
                    suppliers={suppliers}
                    reloadSettings={loadSettings}
                />
            )}
            {activeTab === "import" && (
                <section style={panelStyle}>
                    <h2 style={{ marginTop: 0 }}>Импорт исторического Excel</h2>
                    <div style={{ display: "grid", gap: 10, maxWidth: 520 }}>
                        <Field label="Excel файл">
                            <input
                                style={inputStyle}
                                type="file"
                                accept=".xlsx,.xlsm"
                                onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                            />
                        </Field>
                        <p style={{ color: "#64748b", fontSize: 13, margin: 0 }}>
                            На проде выберите файл Excel перед импортом. Без выбранного файла используется локальный dev-путь.
                        </p>
                    </div>
                    <button type="button" style={primaryButtonStyle} onClick={runImport} disabled={loading}>Импортировать</button>
                </section>
            )}
        </div>
    );
};

export default FinanceReportPage;
